import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define types
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number;
}

export interface Modal {
  id: string;
  type: string;
  props?: Record<string, any>;
}

export interface UIState {
  theme: 'light' | 'dark' | 'system';
  sidebarOpen: boolean;
  notifications: Notification[];
  activeModals: Modal[];
  isLoading: boolean;
  processingProgress: number | null;
  processingMessage: string | null;
}

// Define initial state
const initialState: UIState = {
  theme: 'system',
  sidebarOpen: true,
  notifications: [],
  activeModals: [],
  isLoading: false,
  processingProgress: null,
  processingMessage: null
};

// Create slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const id = Date.now().toString();
      state.notifications.push({
        ...action.payload,
        id
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    openModal: (state, action: PayloadAction<Omit<Modal, 'id'>>) => {
      const id = Date.now().toString();
      state.activeModals.push({
        ...action.payload,
        id
      });
    },
    closeModal: (state, action: PayloadAction<string>) => {
      state.activeModals = state.activeModals.filter(
        modal => modal.id !== action.payload
      );
    },
    closeAllModals: (state) => {
      state.activeModals = [];
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setProcessingProgress: (state, action: PayloadAction<number | null>) => {
      state.processingProgress = action.payload;
    },
    setProcessingMessage: (state, action: PayloadAction<string | null>) => {
      state.processingMessage = action.payload;
    }
  }
});

export const {
  setTheme,
  toggleSidebar,
  setSidebarOpen,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  closeAllModals,
  setLoading,
  setProcessingProgress,
  setProcessingMessage
} = uiSlice.actions;

export default uiSlice.reducer; 